/**
 * File Service Utility
 * Provides functions for accessing files through presigned URLs from the backend API
 */

import { apiService } from './api';
import { authService } from './auth';

/**
 * Check if a value is a file key/path
 * File keys start with "files/" or "uploads/"
 */
export function isFileKey(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string') {
    return false;
  }

  const trimmed = value.trim();
  return trimmed.startsWith('files/') || trimmed.startsWith('uploads/');
}

/**
 * Check if a value is an image ID (from DB) - not a URL, not a file key
 * Used for summary screen eye icons to fetch image via utility/image/{id}/download
 */
export function isImageId(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return false;
  if (isFileKey(trimmed)) return false;
  return true;
}

const getApiBaseUrl = (): string =>
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? '' : 'https://pabauto.garage360.app/api');

/**
 * Get the image download URL for an image ID (pabws/utility/image/{imageId}/download)
 */
export function getImageDownloadUrl(imageId: string): string {
  if (!imageId || typeof imageId !== 'string') {
    throw new Error('Invalid image ID');
  }
  const baseUrl = getApiBaseUrl();
  const encodedId = encodeURIComponent(imageId.trim());
  const path = `/pabws/utility/image/${encodedId}/download`;
  return baseUrl ? `${baseUrl}${path}` : path;
}

/**
 * Open an image in a new tab by ID (streaming response from utility/image/{id}/download)
 */
export async function openImageById(imageId: string): Promise<void> {
  if (!imageId || typeof imageId !== 'string') {
    throw new Error('Invalid image ID');
  }

  const url = getImageDownloadUrl(imageId);

  const encryptedUserData = localStorage.getItem('encryptedUserData') || localStorage.getItem('encryptedData');
  let token = '';
  if (encryptedUserData) {
    try {
      const decrypted = authService.decryptData(encryptedUserData);
      token = decrypted?.token || '';
    } catch {
      token = localStorage.getItem('token') || '';
    }
  } else {
    token = localStorage.getItem('token') || '';
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    if (response.status === 404) throw new Error('Image not found');
    if (response.status === 403) throw new Error('Access denied');
    throw new Error(`Failed to load image: ${response.statusText}`);
  }

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const newWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
  if (!newWindow) {
    window.URL.revokeObjectURL(blobUrl);
    throw new Error('Popup blocked. Please allow popups for this site.');
  }
  newWindow.addEventListener('beforeunload', () => window.URL.revokeObjectURL(blobUrl));
}

/**
 * Fetch image as Blob by ID (for preview in edit mode)
 * @param imageId - The image ID from DB
 * @returns Promise<Blob> - The image blob
 */
export async function getImageBlob(imageId: string): Promise<Blob> {
  if (!imageId || typeof imageId !== 'string') {
    throw new Error('Invalid image ID');
  }

  const url = getImageDownloadUrl(imageId);

  const encryptedUserData = localStorage.getItem('encryptedUserData') || localStorage.getItem('encryptedData');
  let token = '';
  if (encryptedUserData) {
    try {
      const decrypted = authService.decryptData(encryptedUserData);
      token = decrypted?.token || '';
    } catch {
      token = localStorage.getItem('token') || '';
    }
  } else {
    token = localStorage.getItem('token') || '';
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    if (response.status === 404) throw new Error('Image not found');
    if (response.status === 403) throw new Error('Access denied');
    throw new Error(`Failed to load image: ${response.statusText}`);
  }

  return response.blob();
}

const getUploadImageUrl = (): string => {
  const base = import.meta.env.VITE_API_BASE_URL || "https://pabauto.garage360.app/api";
  return base.replace(/\/$/, "") + "/pabws/utility/upload-image";
};

interface UploadImageResponse {
  success: boolean;
  message?: string;
  data?: { imageId: string; storageKey: string };
}

/**
 * Upload an image file to the utility API. Used at form submission time.
 * @param file - The file to upload
 * @returns Promise resolving to the imageId returned by the API
 */
export async function uploadImage(file: File): Promise<string> {
  const imageId = crypto.randomUUID();
  const formData = new FormData();
  formData.append("MultipartImage", file);
  formData.append("imageId", imageId);
  const res = await fetch(getUploadImageUrl(), {
    method: "POST",
    body: formData,
  });
  const json = (await res.json()) as UploadImageResponse;
  if (!json.success || !json.data?.imageId) {
    throw new Error(json.message || "Upload failed");
  }
  return json.data.imageId;
}

/**
 * Get file extension from file key
 */
export function getFileExtension(fileKey: string | null | undefined): string | null {
  if (!fileKey || typeof fileKey !== 'string') {
    return null;
  }

  // Remove query parameters if any
  const pathWithoutQuery = fileKey.split('?')[0].split('#')[0];
  const parts = pathWithoutQuery.split('.');

  if (parts.length < 2) {
    return null;
  }

  return parts[parts.length - 1].toLowerCase();
}

/**
 * Get file icon/emoji based on file extension
 */
export function getFileIcon(fileKey: string | null | undefined): string {
  const extension = getFileExtension(fileKey);

  if (!extension) {
    return '📎'; // Default attachment icon
  }

  const iconMap: Record<string, string> = {
    // PDF
    'pdf': '📄',
    // Excel
    'xls': '📊',
    'xlsx': '📊',
    // Word
    'doc': '📝',
    'docx': '📝',
    // Images
    'jpg': '🖼️',
    'jpeg': '🖼️',
    'png': '🖼️',
    'gif': '🖼️',
    'webp': '🖼️',
    'svg': '🖼️',
    'bmp': '🖼️',
    // Others
    'txt': '📄',
    'csv': '📊',
    'zip': '📦',
    'rar': '📦',
  };

  return iconMap[extension] || '📎';
}

/**
 * Get human-readable file type name
 */
export function getFileTypeName(fileKey: string | null | undefined): string {
  const extension = getFileExtension(fileKey);

  if (!extension) {
    return 'File';
  }

  const typeMap: Record<string, string> = {
    'pdf': 'PDF Document',
    'xls': 'Excel Spreadsheet',
    'xlsx': 'Excel Spreadsheet',
    'doc': 'Word Document',
    'docx': 'Word Document',
    'jpg': 'Image',
    'jpeg': 'Image',
    'png': 'Image',
    'gif': 'Image',
    'webp': 'Image',
    'svg': 'Image',
    'bmp': 'Image',
    'txt': 'Text File',
    'csv': 'CSV File',
    'zip': 'ZIP Archive',
    'rar': 'RAR Archive',
  };

  return typeMap[extension] || extension.toUpperCase() + ' File';
}

/**
 * Extract filename from file key
 */
export function getFileName(fileKey: string | null | undefined): string {
  if (!fileKey || typeof fileKey !== 'string') {
    return 'file';
  }

  // Remove query parameters
  const pathWithoutQuery = fileKey.split('?')[0].split('#')[0];
  const parts = pathWithoutQuery.split('/');
  return parts[parts.length - 1] || 'file';
}

/**
 * URL encode file key for API calls
 * Handles special characters in file paths
 */
function encodeFileKey(fileKey: string): string {
  // Split by '/' and encode each segment separately
  const parts = fileKey.split('/');
  return parts.map(part => encodeURIComponent(part)).join('/');
}

/**
 * Get presigned URL for a file
 * @param fileKey - The file key (e.g., "files/994c38c47eec4fc5bff5a7e3db1aa263.pdf")
 * @returns Promise<string> - The presigned URL
 * @throws Error if API call fails
 */
export async function getPresignedUrl(fileKey: string): Promise<string> {
  if (!fileKey || typeof fileKey !== 'string') {
    throw new Error('Invalid file key');
  }

  try {
    // URL encode the file key properly
    const encodedFileKey = encodeFileKey(fileKey);
    const endpoint = `/pabws/utility/file/${encodedFileKey}/presigned`;

    const response = await apiService.get(endpoint, undefined, {
      includeAuthToken: true,
      authHeaderType: 'Bearer',
      errorHandling: 'throw',
    });

    if (response && response.success && response.data && response.data.presignedUrl) {
      return response.data.presignedUrl;
    }

    throw new Error('Invalid response from presigned URL API');
  } catch (error: any) {
    console.error('[FileService] Error getting presigned URL:', error);

    if (error.response?.status === 404) {
      throw new Error('File not found');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied to file');
    } else if (error.message) {
      throw new Error(`Failed to get file access: ${error.message}`);
    } else {
      throw new Error('Failed to get file access. Please try again.');
    }
  }
}

/**
 * Download a file directly
 * @param fileKey - The file key
 * @param fileName - Optional custom filename for download
 * @returns Promise<void>
 * @throws Error if download fails
 */
export async function downloadFile(
  fileKey: string,
  fileName?: string
): Promise<void> {
  if (!fileKey || typeof fileKey !== 'string') {
    throw new Error('Invalid file key');
  }

  try {
    // URL encode the file key properly
    const encodedFileKey = encodeFileKey(fileKey);
    const endpoint = `/pabws/utility/file/${encodedFileKey}/download`;

    // Use apiService to get the file blob
    // Note: We need to use fetch directly for blob response
    const baseUrl = import.meta.env.VITE_API_BASE_URL ||
      (import.meta.env.DEV ? '' : 'https://pabauto.garage360.app/api');
    const fullUrl = baseUrl ? `${baseUrl}${endpoint}` : endpoint;

    // Get auth token
    const encryptedUserData = localStorage.getItem('encryptedUserData') || localStorage.getItem('encryptedData');
    let token = '';
    if (encryptedUserData) {
      try {
        const decrypted = authService.decryptData(encryptedUserData);
        token = decrypted?.token || '';
      } catch (e) {
        // Fallback to direct token if decryption fails
        token = localStorage.getItem('token') || '';
      }
    } else {
      token = localStorage.getItem('token') || '';
    }

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('File not found');
      } else if (response.status === 403) {
        throw new Error('Access denied to file');
      }
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || getFileName(fileKey);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error('[FileService] Error downloading file:', error);

    if (error.message) {
      throw error;
    } else {
      throw new Error('Failed to download file. Please try again.');
    }
  }
}

/**
 * Open a file in a new tab using presigned URL
 * @param fileKey - The file key
 * @returns Promise<void>
 * @throws Error if opening fails
 */
export async function openFile(fileKey: string): Promise<void> {
  if (!fileKey || typeof fileKey !== 'string') {
    throw new Error('Invalid file key');
  }

  try {
    const presignedUrl = await getPresignedUrl(fileKey);
    const newWindow = window.open(presignedUrl, '_blank', 'noopener,noreferrer');

    if (!newWindow) {
      throw new Error('Popup blocked. Please allow popups for this site.');
    }
  } catch (error: any) {
    console.error('[FileService] Error opening file:', error);
    throw error;
  }
}

/**
 * File Service Interface
 */
export const fileService = {
  getPresignedUrl,
  downloadFile,
  openFile,
  openImageById,
  getImageBlob,
  isFileKey,
  isImageId,
  getImageDownloadUrl,
  getFileIcon,
  getFileExtension,
  getFileTypeName,
  getFileName,
};
